/**
 * Cognito authentication.
 *
 * Uses SRP so the password is never sent to Cognito in the clear. The resulting
 * access token carries `cognito:groups`, which is what the agent resolves the
 * persona from — so the token *is* the authorisation, and nothing the UI says
 * about which persona it thinks the user has affects what the agent will do.
 */

import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import type { AgentName, AppConfig } from "./agui";

export interface Identity {
  accessToken: string;
  email: string;
  /** Display name from the token's `name` claim, if the account has one set. */
  name?: string;
  /** True when the token carries a staff group. */
  isStaff: boolean;
  /** Agents this user may pick, least capable first. */
  agents: AgentName[];
  groups: string[];
  expiresAt: number;
}

/**
 * Must match STAFF_GROUPS in agents/registry.py and the interceptor.
 *
 * This drives which agents the UI *offers*. It is not the control: each Runtime
 * re-resolves entitlement from the verified token, so a student who somehow reached
 * the Student Success Runtime is served Study Guide regardless of what this list
 * says. A list that disagrees with the backend produces a confusing UI, not a
 * privilege escalation.
 */
const STAFF_GROUPS = new Set(["advisor", "advisors", "student-advisor", "staff", "registry"]);

function pool(config: AppConfig): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.clientId,
  });
}

/** Read entitlement from the token, failing closed to Study Guide only. */
function identityFromSession(session: CognitoUserSession, fallbackEmail: string): Identity {
  const accessToken = session.getAccessToken();
  const payload = accessToken.decodePayload() as Record<string, unknown>;

  // The email claim lives on the ID token, not the access token, so read it
  // there and only fall back if absent. (The username is also the email under
  // this pool's username-based sign-in, but the ID token's email is canonical.)
  const idPayload = session.getIdToken()?.decodePayload() as
    | Record<string, unknown>
    | undefined;
  const email =
    typeof idPayload?.email === "string" && idPayload.email
      ? idPayload.email
      : fallbackEmail;

  // The `name` standard attribute (set on the account) rides the ID token when
  // present. Used only for a friendlier header — the email stays the identity key.
  const name =
    typeof idPayload?.name === "string" && idPayload.name ? idPayload.name : undefined;

  const raw = payload["cognito:groups"];
  const groups = Array.isArray(raw) ? raw.map(String) : [];
  const isStaff = groups.some((g) => STAFF_GROUPS.has(g.toLowerCase()));

  // Everyone gets Study Guide: it is scoped to the caller's own record, so a user
  // with no groups at all is a legitimate student. Staff get both, because staff
  // study too. Mirrors entitled_agents() in agents/registry.py.
  const agents: AgentName[] = isStaff ? ["study", "student_success"] : ["study"];

  return {
    accessToken: accessToken.getJwtToken(),
    email,
    name,
    isStaff,
    agents,
    groups,
    expiresAt: accessToken.getExpiration() * 1000,
  };
}

export class NewPasswordRequired extends Error {
  constructor(readonly user: CognitoUser) {
    super("A new password is required for this account.");
  }
}

export function signIn(
  config: AppConfig,
  email: string,
  password: string,
): Promise<Identity> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool(config) });
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(identityFromSession(session, email)),
        onFailure: (err) => reject(new Error(err.message ?? "Sign-in failed.")),
        // Cognito requires a password change on first login for admin-created
        // users, which is how the demo accounts are set up.
        newPasswordRequired: () => reject(new NewPasswordRequired(user)),
      },
    );
  });
}

export function completeNewPassword(
  user: CognitoUser,
  email: string,
  newPassword: string,
): Promise<Identity> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(identityFromSession(session, email)),
      onFailure: (err) => reject(new Error(err.message ?? "Could not set the password.")),
    });
  });
}

/** Restore a session on page load, refreshing the token if needed. */
export function currentIdentity(config: AppConfig): Promise<Identity | null> {
  return new Promise((resolve) => {
    const user = pool(config).getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        resolve(null);
        return;
      }
      const email = user.getUsername();
      resolve(identityFromSession(session, email));
    });
  });
}

export function signOut(config: AppConfig): void {
  pool(config).getCurrentUser()?.signOut();
}

// --- Entra hosted-UI sign-in (authorization code + PKCE) ---------------------
//
// Staff sign in through Entra rather than the native form. The web client is
// public (no secret), so this uses PKCE: we send a code challenge on the way out
// and the verifier on the way back, and no secret ever lives in the browser.
//
// The tokens that come back are cached into amazon-cognito-identity-js via
// setSignInUserSession, so the rest of the app — currentIdentity(), token refresh,
// signOut() — treats an Entra session exactly like a native one.

const VERIFIER_KEY = "edu.pkce.verifier";
const STATE_KEY = "edu.pkce.state";

/** True when the deployment has Entra federation wired (see the frontend stack). */
export function entraConfigured(config: AppConfig): boolean {
  return Boolean(config.cognitoDomain && config.entraProvider);
}

/** The registered redirect URI: this app's origin. Must be in the client's callbackUrls. */
function redirectUri(): string {
  return `${window.location.origin}/`;
}

function randomUrlSafe(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return base64Url(buffer.buffer);
}

function base64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(digest);
}

/** Begin the redirect to Cognito's hosted UI, jumping straight to Entra. */
export async function startEntraSignIn(config: AppConfig): Promise<void> {
  if (!entraConfigured(config)) {
    throw new Error("Entra sign-in is not configured for this deployment.");
  }
  const verifier = randomUrlSafe(48);
  const state = randomUrlSafe(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const url = new URL(`${config.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("redirect_uri", redirectUri());
  url.searchParams.set("scope", "openid email profile");
  // Skip Cognito's provider-picker and go straight to Entra.
  url.searchParams.set("identity_provider", config.entraProvider as string);
  url.searchParams.set("code_challenge", await pkceChallenge(verifier));
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("state", state);
  window.location.assign(url.toString());
}

/** Strip ?code/&state from the address bar so a refresh does not re-run the exchange. */
function clearAuthParamsFromUrl(): void {
  window.history.replaceState({}, document.title, redirectUri());
}

/**
 * If the page loaded on a hosted-UI redirect (`?code=...`), exchange the code for
 * tokens and return the resulting identity. Returns null when there is no code to
 * process, so App can call it unconditionally on boot.
 */
export async function completeHostedUiSignIn(config: AppConfig): Promise<Identity | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (!code) return null;

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  clearAuthParamsFromUrl();

  if (!verifier || params.get("state") !== expectedState) {
    // No verifier or a state mismatch: not a flow we started. Fail closed.
    throw new Error("Sign-in could not be verified. Please try again.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  const response = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!response.ok) {
    throw new Error("Entra sign-in could not be completed.");
  }
  const tokens = (await response.json()) as {
    access_token: string;
    id_token: string;
    refresh_token: string;
  };

  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }),
  });

  // Persist through the SDK so getCurrentUser()/refresh/signOut work uniformly.
  const payload = session.getAccessToken().decodePayload() as Record<string, unknown>;
  const username = typeof payload.username === "string" ? payload.username : String(payload.sub);
  const user = new CognitoUser({ Username: username, Pool: pool(config) });
  user.setSignInUserSession(session);

  return identityFromSession(session, "");
}
